import { useCallback, useRef } from "react";

export interface ReturnFocus {
  /** Remembers what has focus now: call it just before opening a dialog. */
  readonly remember: () => void;
  /** Gives focus back to what was remembered, once the dialog has gone: call it when the dialog closes. */
  readonly restore: () => void;
}

/**
 * Focus goes back where it came from when a dialog closes (issue #261). Mantine's Modal returns focus itself only
 * while it stays mounted and `opened` turns false; a dialog rendered as `{open ? <Dialog /> : null}` unmounts
 * instead, and focus fell to the page body, so a keyboard user started again from the top. `remember` before
 * opening, `restore` on close. Nothing happens if the element has left the page since.
 */
export function useReturnFocus(): ReturnFocus {
  const saved = useRef<HTMLElement | null>(null);
  const remember = useCallback(() => {
    const active = document.activeElement;
    saved.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);
  const restore = useCallback(() => {
    const target = saved.current;
    saved.current = null;
    if (target === null) return;
    // After the dialog's own teardown, which may move focus itself.
    requestAnimationFrame(() => {
      if (target.isConnected) target.focus();
    });
  }, []);
  return { remember, restore };
}
